/* @flow */

import * as React from 'react';
import glamorous from 'glamorous';
import { get } from 'lodash';
import { FEATURED } from 'app/constants/challengeCategoryTypes';
import RewriteNavLink from 'app/components/routing/RewriteNavLink';
import TabSet from 'app/components/commons/TabSet';
import { hover } from 'app/utils/cssUtils';
import SportsFilterTab from './SportsFilterTab';
import LoadingTabs from './LoadingTabs';

type Props = {
  activeFilter?: string,
  activeSport: ?string,
  activeSubCategory: ?string,
  isLoading?: boolean,
  namespace: string,
  showAll?: boolean,
  showFeatured?: boolean,
  showSubCategories?: boolean,
  spartan?: boolean,
  sportsList: Array<SportCategory>
};

const defaultProps = {
  activeFilter: undefined,
  isLoading: false,
  showAll: false,
  showFeatured: false,
  showSubCategories: false,
  spartan: false
};

const Wrapper = glamorous.div({
  overflowX: 'auto'
});

const ContentWrapper = glamorous.div(({ isOpen, theme, spartan }) => ({
  backgroundColor: spartan ? theme.color.spartanBlue : theme.color.panelSecondary,
  height: isOpen ? 42 : 0,
  overflow: 'hidden',
  transition: 'height 0.2s ease-out'
}));

const List = glamorous.div(({ theme, activeIndex, listSize, spartan }) => ({
  display: 'flex',
  position: 'relative',
  width: 100 * listSize,
  '&::after': {
    backgroundColor: spartan ? theme.color.white : theme.color.primary,
    bottom: 0,
    content: '"\u00a0"',
    height: 2,
    left: 0,
    position: 'absolute',
    transform: `translateX(${activeIndex * 100}%)`,
    transition: 'transform 0.3s ease-in-out',
    width: `${100 / listSize}%`
  }
}));

// glamorous navlink example
const StyledLink = glamorous(RewriteNavLink, {
  filterProps: ['spartan'],
  withProps: { exact: true }
})(
  {
    width: 100,
    padding: 10,
    textTransform: 'uppercase',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    textAlign: 'center'
  },
  ({ theme, spartan }) => ({
    backgroundColor: spartan ? theme.color.spartanBlue : theme.color.panelSecondary,
    fontSize: theme.fontSize.small
  }),
  ({ disabled, theme }) =>
    disabled
      ? {
          pointerEvents: 'none',
          color: 'black'
        }
      : {
          color: theme.color.panelAccent,
          ...hover({
            opacity: 0.8
          }),
          // auto added by NavLink
          '.active': {
            color: theme.color.panelSelected
          }
        }
);

function FilterBox({
  activeFilter,
  activeSport,
  activeSubCategory,
  isLoading,
  namespace,
  showAll,
  showFeatured,
  showSubCategories,
  sportsList,
  spartan
}: Props) {
  if (isLoading) {
    return <LoadingTabs numTabs={9} height={110} />;
  }

  const selectedSport = sportsList.find(s => s.slug === activeSport) || null;
  const activeSubCategories = get(selectedSport, 'subcategories') || [];

  return (
    <Wrapper>
      <TabSet>
        {showAll && (
          <SportsFilterTab
            active={!activeSport && !activeFilter}
            href={namespace}
            icon="icon-sport-all"
          >
            All
          </SportsFilterTab>
        )}
        {showFeatured && (
          <SportsFilterTab
            active={!activeSport && activeFilter === FEATURED}
            href={namespace}
            icon="icon-sport-all"
          >
            Featured
          </SportsFilterTab>
        )}
        {sportsList.map(sport => (
          <SportsFilterTab
            key={sport.id}
            active={sport.slug === activeSport}
            disabled={sport.disabled}
            href={`${namespace}/${sport.slug}`}
            icon={
              spartan && sport.slug === activeSport ? 'icon-spartan' : `icon-sport-${sport.icon}`
            }
            spartan={spartan}
          >
            {sport.subcategories.length === 1 ? sport.subcategories[0].name : sport.name}
          </SportsFilterTab>
        ))}
      </TabSet>
      <ContentWrapper
        isOpen={showSubCategories && activeSubCategories.length > 1}
        spartan={spartan}
      >
        <List
          activeIndex={
            activeSubCategories.findIndex(cat => cat.slug === activeSubCategory) + (showAll ? 1 : 0)
          }
          listSize={activeSubCategories.length + 1}
          spartan={spartan}
        >
          {showAll && (
            <StyledLink spartan={spartan} to={`${namespace}/${get(selectedSport, 'slug')}`}>
              All
            </StyledLink>
          )}
          {activeSubCategories.map(subCategory => (
            <StyledLink
              key={subCategory.name}
              to={`${namespace}/${selectedSport.slug}/${subCategory.slug}`}
              disabled={subCategory.disabled}
              spartan={spartan}
            >
              {subCategory.name}
            </StyledLink>
          ))}
        </List>
      </ContentWrapper>
    </Wrapper>
  );
}

FilterBox.defaultProps = defaultProps;

export default FilterBox;
